// External Imports
import { Fragment, useEffect, useRef, useState } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import uploadFile from "../../util/upload-file";
import Heading from "../UI/Heading";
import ImageCarousel from "../UI/ImageCarousel";
import LoadingSpinner from "../UI/LoadingSpinner";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";

/** ========================================================================
 * * EndBooking Component
 * Renders a form, which allows changing a booking status to ended.
 * ========================================================================== */

export default function EndBooking({ setOpen, booking, onComplete }) {
  // Initialize States
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [item, setItem] = useState();
  const [currentMileage, setCurrentMileage] = useState(0);
  const [currentFuel, setCurrentFuel] = useState(0);
  const [charge, setCharge] = useState(false);

  /* ======================= FETCH VEHICLE STATS ======================= */
  const calculateTotals = () => {
    let milesDriven = currentMileage - booking.startMileage;
    if (milesDriven < 0) milesDriven = 0;
    let extraMiles = milesDriven - booking.milesIncluded;
    if (extraMiles < 0) extraMiles = 0;
    const extraMilesCharge = Number(
      (extraMiles * booking.mileageRate).toFixed(2)
    ).toFixed(2);
    document.getElementById("mileage").value = currentMileage;
    document.getElementById("milesDriven").value = milesDriven;
    document.getElementById("extraMiles").value = extraMiles;
    document.getElementById("extraMilesCharge").value = extraMilesCharge;
    setCharge(extraMilesCharge > 0);
  };

  /* ======================= FETCH VEHICLE STATS ======================= */
  const fetchVehicleStats = async () => {
    try {
      setLoading(true);
      // Send Request
      const response = await requestAdminAPI("fetch-vehicle-reports", {
        vehicles: [
          { vin: booking.vehicle.vin, trackerId: booking.vehicle.trackerId },
        ],
      });
      const report = response[0]?.report;
      if (report) {
        setCurrentMileage(report.mileage);
        setCurrentFuel(report.fuel);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };
  /* ======================= END BOOKING ======================= */
  const confirmHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Get Image URLs
      const imageUrls = [];
      /*
       * Upload images directly to the database
       * and get the URL of the uploaded images.
       */
      if (images.length > 0) {
        const path = `bookings/${booking.id}/vehicle_images_`;
        await Promise.all(
          images.map(async (image, index) => {
            setItem(image.name);
            const type = image.type.split("/")[0];
            const extension = image.name.split(".").pop();
            const url = await await uploadFile(
              `${path}_${new Date().toISOString()}_${index + 1}-${type}.${extension}`,
              image,
              setUploadProgress
            );
            imageUrls.push(url);
          })
        );
      }
      // Create Payload
      const payload = {
        bookingId: booking.id,
        checkOutImages: imageUrls,
        endMileage: Number(document.getElementById("mileage").value),
        endFuel: Number(document.getElementById("fuel").value),
        rating: Number(document.getElementById("rating").value),
        extraMilesCharge: charge
          ? Number(document.getElementById("extraMilesCharge")?.value || 0)
          : 0,
        extraMiles: Number(document.getElementById("extraMiles")?.value || 0),
        daysUntilDue: Number(
          document.getElementById("daysUntilDue")?.value || 3
        ),
      };

      // Send Request
      await requestAdminAPI("end-booking", payload);
      setOpen(false);
      onComplete && onComplete();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      setUploadProgress(false);
    }
  };

  useEffect(() => {
    fetchVehicleStats();
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [currentMileage]);

  return (
    <Fragment>
      <Heading
        title="End Booking"
        text="Mileage and Fuel are automatically fetched from trackers."
        isBackArrow={false}
      >
        <MdClose className="icon" onClick={() => setOpen(false)} />
      </Heading>

      <div className="grid gap-8 text-sm">
        <ImageCarousel images={images} setImages={setImages} preview={true} />
        <div className="info">
          <DetailPair
            label="Start Mileage (mi)"
            edit={true}
            inputOptions={{
              disabled: true,
              value: `${booking.startMileage}mi`,
            }}
          />
          <DetailPair
            label="Miles Included"
            edit={true}
            inputOptions={{
              disabled: true,
              value: `${booking.milesIncluded}mi`,
            }}
          />
          <DetailPair
            label="Miles Driven (mi)"
            edit={true}
            inputOptions={{
              id: "milesDriven",
              disabled: true,
            }}
          />
          <DetailPair
            label="Extra Miles (mi)"
            edit={true}
            inputOptions={{
              id: "extraMiles",
              disabled: true,
            }}
          />
          <DetailPair
            label="Extra Miles Charge ($)"
            edit={true}
            inputOptions={{
              id: "extraMilesCharge",
            }}
          />
          <DetailPair
            label="End Mileage (mi)"
            edit={true}
            inputOptions={{
              id: "mileage",
              type: "number",
              onChange: (e) => setCurrentMileage(Number(e.target.value)),
            }}
          />
          <DetailPair
            label="Current Fuel Level (%)"
            edit={true}
            inputOptions={{
              id: "fuel",
              type: "number",
              value: currentFuel,
              onChange: (e) => setCurrentFuel(Number(e.target.value)),
            }}
          />
          <SelectPair
            label="Rating out of 5"
            edit={true}
            options={[
              { id: 5, name: "5 - Excellent" },
              { id: 4, name: "4 - Very Good" },
              { id: 3, name: "3 - Good" },
              { id: 2, name: "2 - Satisfactory" },
              { id: 1, name: "1 - Poor" },
            ]}
            inputOptions={{
              id: "rating",
              defaultValue: 5,
            }}
          />
        </div>
        <div className="flex items-center space-x-4">
          <input
            type="checkbox"
            id="sendInvoice"
            className="checkbox"
            checked={charge}
            onChange={(e) => setCharge(e.target.checked)}
          />
          <p className="text-sm text-stone-400">Charge for Extra Miles</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center space-x-4">
          <LoadingSpinner />
          {uploadProgress && (
            <p className="text-sm text-stone-400">
              Uploading {item}... {uploadProgress}%
            </p>
          )}
        </div>
      ) : (
        <div className="info">
          <div className="btn-secondary" onClick={() => setOpen(false)}>
            Cancel
          </div>
          <button className="btn-primary" onClick={confirmHandler}>
            End Booking
          </button>
        </div>
      )}
    </Fragment>
  );
}
